import { TouchableOpacity, useWindowDimensions } from "react-native";
import TextInput, { Icon as TextInputIcon } from "./TextInput";
import Modal from "../components/Modal";
import { ReactNode, useState } from "react";
import { View } from "react-native";
import DatePicker from "react-native-modern-datepicker";
import { Text } from "react-native-paper";
import { colors } from "../../theme";
import Button from "./Button";
import { formatDate, formatToISO8601 } from "../utils/dateUtils";
import ActionsContainer from "./ActionsContainer";
import moment, { Moment } from "moment";

interface SharedProps {
  description: string;
  onChange(date: string): void;
  title: string;
  value?: string;
  validate?(date: Moment): string | ReactNode | undefined;
}

interface ModalProps extends SharedProps {
  onDismiss(): void;
  visible: boolean;
}

export interface Props extends SharedProps {
  error?: { message?: string };
  onBlur(): void;
  placeholder: string;
  testID?: string;
}

const mainColor = colors.primary.DEFAULT;
const selectedTextColor = colors.sky.white;
const borderColor = colors.sky.dark;

export function getDateFromParam(value?: string) {
  if (!value) return;

  const date = moment(value);
  if (date.isValid()) return formatToISO8601(date);
}

function DatePickerModal({ validate = () => undefined, visible, onDismiss, onChange, description, value, title }: ModalProps) {
  const [date, setDate] = useState(getDateFromParam(value));
  const [errorMsg, setErrorMsg] = useState<string | ReactNode>("");
  const { width } = useWindowDimensions();
  const [isValid, setIsValid] = useState(true);

  function onCloseMessage() {
    setErrorMsg("");
  }

  return (
    <Modal {...{ title, visible, onDismiss }}>
      <View className="bg-sky-white" testID="DatePickerModal">
        <View className="m-6">
          <Text className="text-small-normal font-400 text-ink-dark">{description}</Text>
        </View>
        <DatePicker
          key={`date-picker-${width}`}
          selected={date}
          current={date || formatToISO8601(moment())}
          options={{
            mainColor,
            selectedTextColor,
            borderColor,
          }}
          onSelectedChange={(dateString: string) => {
            const dateMoment = moment(dateString, "YYYY/MM/DD");
            const errorMessage = validate(dateMoment);
            setIsValid(!errorMessage);
            setErrorMsg(errorMessage || "");
            setDate(formatToISO8601(dateMoment));
          }}
          mode="calendar"
        />

        <ActionsContainer
          errorTitle="Invalid Date"
          errorMessage={errorMsg}
          onCloseErrorMessage={onCloseMessage}
          errorAlert
          horizontalStyle="all"
          insideModal
        >
          <Button onPress={onDismiss}>Cancel</Button>
          <Button
            onPress={() => {
              if (date && isValid) onChange(date);
              onDismiss();
            }}
            mode="contained"
            disabled={!date || !isValid}
          >
            Save
          </Button>
        </ActionsContainer>
      </View>
    </Modal>
  );
}

export default function DatePickerInput({ validate, onChange, value, title, description, testID, ...rest }: Props) {
  const [showModal, setShowModal] = useState(false);

  const onPress = () => {
    setShowModal(true);
  };

  return (
    <TouchableOpacity activeOpacity={0.7} {...{ testID, onPress }}>
      <TextInput
        editable={false}
        pointerEvents="none"
        right={<TextInputIcon icon="calendar" onPress={onPress} />}
        value={value && formatDate(value, "MM/DD/YYYY")}
        {...rest}
      />
      <DatePickerModal
        visible={showModal}
        onDismiss={() => {
          setShowModal(false);
        }}
        {...{ onChange, value, title, description, validate }}
      />
    </TouchableOpacity>
  );
}
